
import Hero from "@/components/Hero";
import GameShowcase from "@/components/GameShowcase";
import Features from "@/components/Features";
import Download from "@/components/Download";
import VictoryShowcase from "@/components/VictoryShowcase";
import Community from "@/components/Community";
import Footer from "@/components/Footer";
import { useIsMobile } from "@/hooks/use-mobile";
import { useEffect, useRef, useState } from "react";
import Navigation from "@/components/Navigation";
import { Helmet } from "react-helmet-async";

const Index = () => {
  const isMobile = useIsMobile();
  const mainRef = useRef<HTMLElement>(null);
  const footerRef = useRef<HTMLElement>(null);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFooterVisible(entry.isIntersecting);
      },
      {
        threshold: 0.1,
      }
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => {
      if (footerRef.current) {
        observer.unobserve(footerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isMobile) return;
    let isScrolling = false;
    let startY = 0;
    const threshold = 50;
    const handleWheel = (e: WheelEvent) => {
      if (isScrolling) return;
      const sections = Array.from(mainRef.current?.children || []);
      const currentSection = sections.findIndex(section => {
        const rect = section.getBoundingClientRect();
        return rect.top >= -100 && rect.top <= 100;
      });
      if (currentSection === -1) return;
      if (Math.abs(e.deltaY) > threshold) {
        isScrolling = true;
        const nextSection = e.deltaY > 0 ? Math.min(currentSection + 1, sections.length - 1) : Math.max(currentSection - 1, 0);
        sections[nextSection].scrollIntoView({
          behavior: 'smooth'
        });
        setTimeout(() => {
          isScrolling = false;
        }, 2000);
      }
    };
    const handleTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (isScrolling) return;
      const deltaY = startY - e.touches[0].clientY;
      if (Math.abs(deltaY) > threshold) {
        const sections = Array.from(mainRef.current?.children || []);
        const currentSection = sections.findIndex(section => {
          const rect = section.getBoundingClientRect();
          return rect.top >= -100 && rect.top <= 100;
        });
        if (currentSection === -1) return;
        isScrolling = true;
        const nextSection = deltaY > 0 ? Math.min(currentSection + 1, sections.length - 1) : Math.max(currentSection - 1, 0);
        sections[nextSection].scrollIntoView({
          behavior: 'smooth'
        });
        setTimeout(() => {
          isScrolling = false;
        }, 2000);
      }
    };
    const main = mainRef.current;
    if (main) {
      main.addEventListener('wheel', handleWheel);
      main.addEventListener('touchstart', handleTouchStart);
      main.addEventListener('touchmove', handleTouchMove);
      return () => {
        main.removeEventListener('wheel', handleWheel);
        main.removeEventListener('touchstart', handleTouchStart);
        main.removeEventListener('touchmove', handleTouchMove);
      };
    }
  }, [isMobile]);

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <Helmet>
        <title>Front Runner: Racehorse Manager | Horse Racing Management Game</title>
        <meta name="description" content="Experience the thrill of horse racing management like never before. Build your stable, train your horses, and compete in epic races in Front Runner: Racehorse Manager." />
        <meta name="keywords" content="horse racing game, racehorse manager, racing simulation, horse training, stable management" />
        
        {/* Primary Meta Tags */}
        <meta itemProp="name" content="Front Runner: Racehorse Manager | Horse Racing Management Game" />
        <meta itemProp="description" content="Experience the thrill of horse racing management like never before. Build your stable, train your horses, and compete in epic races." />
        <meta itemProp="image" content="https://c0b7ec99-c595-43b9-be02-b9d207bd20c5.lovableproject.com/lovable-uploads/FRRM-trailerImage.webp" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://frontrunnergame.com/" />
        <meta property="og:title" content="Front Runner: Racehorse Manager | Horse Racing Management Game" />
        <meta property="og:description" content="Experience the thrill of horse racing management like never before. Build your stable, train your horses, and compete in epic races." />
        <meta property="og:image" content="https://c0b7ec99-c595-43b9-be02-b9d207bd20c5.lovableproject.com/lovable-uploads/FRRM-trailerImage.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://frontrunnergame.com/" />
        <meta name="twitter:title" content="Front Runner: Racehorse Manager | Horse Racing Management Game" />
        <meta name="twitter:description" content="Experience the thrill of horse racing management. Build your stable, train horses, compete in races." />
        <meta name="twitter:image" content="https://c0b7ec99-c595-43b9-be02-b9d207bd20c5.lovableproject.com/lovable-uploads/FRRM-trailerImage.webp" />
        
        {/* Canonical link */}
        <link rel="canonical" href="https://frontrunnergame.com/" />
      </Helmet>
      <Navigation className={`transition-opacity duration-300 ${isFooterVisible ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} />
      <main 
        ref={mainRef} 
        className={`relative z-10 min-h-screen text-white overflow-x-hidden ${!isMobile ? "snap-y snap-mandatory h-screen overflow-y-scroll scroll-smooth" : ""}`} 
        style={!isMobile ? {
          scrollSnapType: 'y mandatory',
          scrollBehavior: 'smooth',
          overflowY: 'scroll',
          overscrollBehaviorY: 'contain'
        } : undefined}
      >
        <section id="home" aria-label="Home" className={`${!isMobile ? "snap-start h-screen" : ""} relative bg-racing-dark`}>
          <Hero />
        </section>
        
        <div className="relative bg-racing-dark">
          <div 
            className="absolute inset-0 w-full z-0" 
            style={{
              backgroundImage: `url('/lovable-uploads/FRRM-website-BG.webp')`,
              backgroundPosition: 'center top',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              minHeight: !isMobile ? '400vh' : '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0
            }} 
          />
          
          <section id="game-experience" aria-label="Game Experience" className={`${!isMobile ? "snap-start min-h-screen" : ""} relative z-1`}>
            <GameShowcase />
          </section>
          
          <section id="features" aria-label="Features & Benefits" className={`${!isMobile ? "snap-start min-h-screen" : ""} relative z-1 mt-24`}>
            <Features />
          </section>
        </div>
        
        <section 
          id="victory" 
          aria-label="Victory & Achievements" 
          className={`${!isMobile ? "snap-start h-screen" : "min-h-screen"} relative bg-racing-dark overflow-hidden`} 
          style={{
            backgroundImage: `url('/lovable-uploads/BG.webp')`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover'
          }}
        >
          <VictoryShowcase />
        </section>
        
        <section id="community" aria-label="Community" className={`${!isMobile ? "snap-start h-screen" : "min-h-screen"} relative bg-racing-dark`}>
          <Community />
        </section>

        <section 
          ref={footerRef}
          id="footer" 
          aria-label="Footer" 
          className={`${!isMobile ? "snap-start min-h-screen" : ""} relative bg-racing-dark flex items-center`}
        >
          <Footer />
        </section>
      </main>
    </div>
  );
};

export default Index;
